import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { uniqueId } from '../form/ids';
import { IconComponent } from '../icon/icon.component';

const POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
];

/** Una riga della matrice di FR-017. */
interface PermissionRow {
  readonly operation: string;
  readonly admin: string;
  readonly participant: string;
  readonly viewer: string;
}

/** La matrice dei permessi del cantiere (FR-017), con le parole della tavola 3A. */
export const PERMISSION_MATRIX: readonly PermissionRow[] = [
  {
    operation: 'Vedere il cantiere e chi vi partecipa',
    admin: 'Sì',
    participant: 'Sì',
    viewer: 'Sì',
  },
  {
    operation: 'Lavorare nelle App a cui è abilitato',
    admin: 'Sì',
    participant: 'Sì',
    viewer: 'Sola lettura',
  },
  { operation: 'Modificare i dati del cantiere', admin: 'Sì', participant: 'No', viewer: 'No' },
  {
    operation: 'Invitare, sospendere e rimuovere partecipanti',
    admin: 'Sì',
    participant: 'No',
    viewer: 'No',
  },
  {
    operation: 'Archiviare, eliminare e ripristinare il cantiere',
    admin: 'Sì',
    participant: 'No',
    viewer: 'No',
  },
  {
    operation: 'Abbandonare il cantiere',
    admin: 'Se non è l’unico',
    participant: 'Sì',
    viewer: 'Sì',
  },
];

/**
 * Aiuto sui permessi (FR-017a): un pulsante "?" che apre, al clic, il confronto completo dei tre
 * ruoli funzionali. Il pulsante ha `aria-expanded` e `aria-controls`; il riquadro tiene il fuoco
 * e si chiude con Esc, con il clic fuori o con "Chiudi", restituendo il fuoco al pulsante.
 */
@Component({
  selector: 'bs-permissions-help',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, CdkTrapFocus, IconComponent],
  host: { class: 'inline-flex align-middle' },
  template: `
    <button
      #trigger
      type="button"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      class="group inline-flex size-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ink dark:focus-visible:outline-bsite-400"
      aria-label="Cosa può fare ogni permesso"
      aria-haspopup="dialog"
      [attr.aria-expanded]="open()"
      [attr.aria-controls]="open() ? panelId : null"
      [attr.data-testid]="testId()"
      (click)="toggle()"
    >
      <span
        class="inline-flex size-6 items-center justify-center rounded-full bg-stone-200 text-xs font-bold text-ink group-hover:bg-stone-300 dark:bg-stone-700 dark:text-stone-50 dark:group-hover:bg-stone-600"
        aria-hidden="true"
        >?</span
      >
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="open()"
      [cdkConnectedOverlayPositions]="positions"
      (overlayOutsideClick)="onOutsideClick($event)"
      (overlayKeydown)="onKeydown($event)"
      (detach)="close()"
    >
      <div
        [id]="panelId"
        role="dialog"
        [attr.aria-labelledby]="headingId"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        tabindex="-1"
        class="w-md max-w-screen rounded-2xl border border-stone-200 bg-white p-4 shadow-lg dark:border-stone-700 dark:bg-stone-900"
        [attr.data-testid]="testId() ? testId() + '-panel' : null"
      >
        <div class="mb-3 flex items-center justify-between gap-2">
          <h2 [id]="headingId" class="text-base font-semibold text-ink dark:text-stone-50">
            Cosa può fare ogni permesso
          </h2>
          <button
            type="button"
            class="inline-flex size-11 items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800"
            aria-label="Chiudi"
            (click)="close()"
          >
            <bs-icon name="close" [size]="18" />
          </button>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <caption class="sr-only">
              Permessi per ruolo nel cantiere
            </caption>
            <thead>
              <tr class="text-xs text-stone-600 dark:text-stone-300">
                <th scope="col" class="py-2 pr-3 font-semibold">Operazione</th>
                <th scope="col" class="px-2 py-2 font-semibold">Amministratore</th>
                <th scope="col" class="px-2 py-2 font-semibold">Partecipante</th>
                <th scope="col" class="px-2 py-2 font-semibold">Visualizzatore</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-stone-200 dark:divide-stone-800">
              @for (row of matrix; track row.operation) {
                <tr class="text-ink dark:text-stone-100">
                  <th scope="row" class="py-2 pr-3 font-normal">{{ row.operation }}</th>
                  <td class="px-2 py-2">{{ row.admin }}</td>
                  <td class="px-2 py-2">{{ row.participant }}</td>
                  <td class="px-2 py-2">{{ row.viewer }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <p class="mt-3 text-sm text-stone-600 dark:text-stone-300">
          I permessi dentro ogni App (per esempio chi convalida un'accettazione) li decide
          l'Amministratore dell'App. Chi entra dalla commessa ha il permesso corrispondente:
          Collaboratore = Partecipante, Lettore = Visualizzatore.
        </p>
      </div>
    </ng-template>
  `,
})
export class PermissionsHelpComponent {
  readonly testId = input<string | null>(null);

  protected readonly matrix = PERMISSION_MATRIX;
  protected readonly positions = POSITIONS;
  protected readonly panelId = uniqueId('bs-permissions-help');
  protected readonly headingId = `${this.panelId}-heading`;
  readonly open = signal(false);
  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.open.set(true);
    }
  }

  close(): void {
    if (!this.open()) {
      return;
    }
    this.open.set(false);
    this.trigger().nativeElement.focus();
  }

  protected onOutsideClick(event: MouseEvent): void {
    if (!this.trigger().nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}
